import { AppButton, AppPagination, AppTable } from '@/components/atoms';
import ClinicLocationsFilterPanel from '@/components/organisms/ClinicLocationsFilterPanel';
import type { ClinicLocationsFilterState } from '@/components/organisms/ClinicLocationsFilterPanel';
import { AdminClinicsListTemplate } from '@/components/templates';
import { SERVICE_TYPE_FALLBACK } from '@/constants/supportRequest';
import { useAdminClinicLocationsListQuery } from '@/hooks/admin/useClinicLocationCreate';
import useAppToast from '@/hooks/useAppToast';
import type { ClinicLocationListItem } from '@/services/admin/clinic-locations';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { flushSync } from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

const DEFAULT_PER_PAGE = 20;

const parsePageFromSearchParams = (searchParams: URLSearchParams): number => {
  const raw = Number(searchParams.get('page') ?? '1');
  if (!Number.isFinite(raw) || raw < 1) return 1;
  return Math.floor(raw);
};

const parseSiteTypesFromSearchParams = (searchParams: URLSearchParams): string[] => {
  const raw = searchParams.get('site_types') ?? '';
  if (!raw) return [];
  return raw
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
};

const getFiltersFromSearchParams = (
  searchParams: URLSearchParams,
  canUseSiteTypeFeatures: boolean
): ClinicLocationsFilterState => {
  const availableDateRaw = searchParams.get('available_date') ?? '';
  const availableDate = availableDateRaw ? dayjs(availableDateRaw) : null;
  return {
    locationName: searchParams.get('location_name') ?? '',
    availableDate: availableDate && availableDate.isValid() ? availableDate : null,
    siteTypes: canUseSiteTypeFeatures ? parseSiteTypesFromSearchParams(searchParams) : [],
  };
};

export default function SiteAdminClinicLocationsListPage() {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { isLoading: isAuthLoading } = useSiteAdminAuth();
  const toast = useAppToast();
  const siteType = site ?? 'medix';

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');

  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : (site ?? '')],
    queryFn: () => getTenantSiteTypes(isSuperAdminRoute ? undefined : (site ?? undefined)),
    enabled: !isAuthLoading && !!site,
    staleTime: Infinity,
  });

  const tenantSiteTypeList: TenantSiteTypeItem[] = siteTypesQuery.data?.data?.site_types ?? [];

  const canUseSiteTypeFeatures = tenantSiteTypeList.length > 1;

  const tenants = tenantSiteTypeList;
  const tenantNameBySiteType = useMemo(() => {
    const map = new Map<string, string>();
    tenantSiteTypeList.forEach(t => {
      if (t.site_type) map.set(t.site_type, t.name);
    });
    return map;
  }, [tenantSiteTypeList]);

  useEffect(() => {
    if (siteTypesQuery.isError) {
      toast.error('サイト一覧の取得に失敗しました。');
    }
  }, [siteTypesQuery.isError, toast]);

  const [filters, setFilters] = useState<ClinicLocationsFilterState>(() =>
    getFiltersFromSearchParams(searchParams, canUseSiteTypeFeatures)
  );
  const [appliedFilters, setAppliedFilters] = useState<ClinicLocationsFilterState>(() =>
    getFiltersFromSearchParams(searchParams, canUseSiteTypeFeatures)
  );
  const [page, setPage] = useState(parsePageFromSearchParams(searchParams));

  useEffect(() => {
    const nextPage = parsePageFromSearchParams(searchParams);
    const nextFilters = getFiltersFromSearchParams(searchParams, canUseSiteTypeFeatures);
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPage(nextPage);
  }, [searchParams, canUseSiteTypeFeatures]);

  const params = useMemo(
    () => ({
      page,
      per_page: DEFAULT_PER_PAGE,
      location_name: appliedFilters.locationName || undefined,
      available_date: appliedFilters.availableDate
        ? appliedFilters.availableDate.format('YYYY-MM-DD')
        : undefined,
      site_types: canUseSiteTypeFeatures
        ? appliedFilters.siteTypes.length > 0
          ? appliedFilters.siteTypes
          : undefined
        : undefined,
    }),
    [page, appliedFilters, canUseSiteTypeFeatures]
  );

  const listQuery = useAdminClinicLocationsListQuery(siteType, params);
  const rows: ClinicLocationListItem[] = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta ?? {
    current_page: 1,
    last_page: 1,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
  };

  const handleSearch = () => {
    const next = new URLSearchParams(searchParams);
    next.set('page', '1');

    const nextLocationName = filters.locationName.trim();
    if (nextLocationName) next.set('location_name', nextLocationName);
    else next.delete('location_name');

    const nextAvailableDate = filters.availableDate
      ? filters.availableDate.format('YYYY-MM-DD')
      : '';
    if (nextAvailableDate) next.set('available_date', nextAvailableDate);
    else next.delete('available_date');

    if (canUseSiteTypeFeatures && filters.siteTypes.length > 0)
      next.set('site_types', filters.siteTypes.join(','));
    else next.delete('site_types');

    const synced = getFiltersFromSearchParams(next, canUseSiteTypeFeatures);
    flushSync(() => {
      setPage(1);
      setAppliedFilters(synced);
      setFilters(synced);
      setSearchParams(next);
    });
    void queryClient.refetchQueries({
      queryKey: ['admin', 'clinic-locations', 'list', siteType],
      type: 'active',
    });
  };

  const breadcrumbItems: BreadcrumbItemType[] = [{ title: '拠点一覧' }];

  const columns: ColumnsType<ClinicLocationListItem> = [
    {
      title: '拠点番号',
      key: 'display_id',
      render: (_, record) => {
        const clinicNumber = record.clinic_number?.trim() ?? '';
        const serialNumber =
          record.serial_number != null && Number.isFinite(record.serial_number)
            ? String(record.serial_number)
            : '';
        if (clinicNumber && serialNumber) return `${clinicNumber}_${serialNumber}`;
        if (serialNumber) return serialNumber;
        return '-';
      },
    },
    { title: '治療院ID', dataIndex: 'clinic_number', key: 'clinic_number' },
    {
      title: 'サイト区分',
      dataIndex: 'site_type',
      key: 'site_type',
      hidden: !canUseSiteTypeFeatures,
      render: (_: unknown, record: ClinicLocationListItem) =>
        (record.site_type && tenantNameBySiteType.get(record.site_type)) || record.site_type || '-',
    },
    {
      title: 'サービス区分',
      dataIndex: 'service_type',
      key: 'service_type',
      render: (_, record) => {
        const rawServiceType = record.service_type ?? '';
        if (!rawServiceType) return '';
        const key = rawServiceType.toString().toUpperCase();
        return renderTableCellTruncate(SERVICE_TYPE_FALLBACK[key] ?? rawServiceType, {
          lineClamp: 1,
          width: 120,
        });
      },
    },
    {
      title: '拠点名',
      dataIndex: 'location_name',
      key: 'location_name',
      render: (_, record) =>
        renderTableCellTruncate(record.location_name || null, {
          lineClamp: 1,
          width: 150,
        }),
    },
    {
      title: '住所',
      key: 'address',
      render: (_, record) =>
        renderTableCellTruncate(`${record.address1 || ''}${record.address2 || ''}`.trim() || null, {
          lineClamp: 1,
          width: 150,
        }),
    },
    {
      title: '有効期限',
      key: 'period',
      render: (_, record) => formatPeriodDisplay(record.available_from, record.available_to),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <span className="flex flex-wrap gap-1">
          <AppButton
            type="primary"
            size="small"
            className="py-1 px-2 my-1"
            onClick={() => navigate(`/${siteType}/admin/clinic_locations/${record.id}`)}
          >
            施術管理者・施術者
          </AppButton>
          <AppButton
            size="small"
            className="py-1 px-2 my-1"
            onClick={() => navigate(`/${siteType}/admin/clinic_locations/${record.id}/edit`)}
          >
            拠点編集
          </AppButton>
        </span>
      ),
    },
  ];

  return (
    <AdminClinicsListTemplate
      title="拠点一覧"
      breadcrumbItems={breadcrumbItems}
      filter={
        <ClinicLocationsFilterPanel
          tenants={tenants}
          filters={filters}
          onChangeFilters={next => setFilters(prev => ({ ...prev, ...next }))}
          onSearch={handleSearch}
        />
      }
      table={
        <div className="bg-white rounded-xl border">
          <AppTable<ClinicLocationListItem>
            columns={columns}
            dataSource={rows}
            rowKey="id"
            loading={listQuery.isLoading}
          />
          {meta && (
            <div className="p-4 flex justify-end">
              <AppPagination
                current={meta.current_page}
                total={meta.total}
                pageSize={meta.per_page || DEFAULT_PER_PAGE}
                hideOnSinglePage
                onChange={nextPage => {
                  const next = new URLSearchParams(searchParams);
                  next.set('page', String(nextPage));
                  setSearchParams(next);
                }}
              />
            </div>
          )}
        </div>
      }
    />
  );
}
